import omit from 'lodash/omit';

import { isBookingProcessAlias } from '../../transactions/transaction';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  getStartOfWeek,
  monthIdString,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';
import { storableError } from '../../util/errors';
import { uniqueBy } from '../../util/generators';
import * as log from '../../util/log';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  fetchStripeAccount,
  updateStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser, fetchCurrentUserHasListingsSuccess } from '../../ducks/user.duck';
import {
  createAlgoliaObject,
  apiBaseUrl,
  onGetCategories,
  onGetDealsCategoriesList,
  onGetStockImages,
  onUpdateBusinessProfile,
  onUpdateCategoriesProfile,
  onUpdateDealCategoriesProfile,
} from '../../util/api';

const { UUID } = sdkTypes;

import {
  BUSINESS_PROFILE_LISTING_TYPE,
  DEAL_LISTING_TYPE,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PUBLISHED,
  PRODUCT_LISTING_TYPE,
  SERVICE_LISTING_TYPE,
  STAFF_LISTING_TYPE,
} from '../../util/types';
import moment from 'moment';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { isArray } from 'lodash';
import { updateListing } from '../EditListingPage/EditListingPage.duck';
import { TEMPLATE_IMAGE_FETCH_LIMIT } from '../EditListingPage/EditListingWizard/templateImageHelpers';

const draftId = '00000000-0000-0000-0000-000000000000';

// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
      uploadedImages: {},
      uploadedImagesOrder: [],
    }
    : {
      uploadedImages,
      uploadedImagesOrder,
    };
};

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

// When navigating through weekly calendar,
// we want to merge new week-related data (inProgres, error) to weeklyExceptionQueries hashmap.
const mergeToWeeklyExceptionQueries = (weeklyExceptionQueries, weekStartId, newDataProps) => {
  return weekStartId
    ? {
      weeklyExceptionQueries: {
        ...weeklyExceptionQueries,
        [weekStartId]: {
          ...weeklyExceptionQueries[weekStartId],
          ...newDataProps,
        },
      },
    }
    : {};
};
// When navigating through monthly calendar (e.g. when adding a new AvailabilityException),
// we want to merge new month-related data (inProgres, error) to monthlyExceptionQueries hashmap.
const mergeToMonthlyExceptionQueries = (monthlyExceptionQueries, monthId, newDataProps) => {
  return monthId
    ? {
      monthlyExceptionQueries: {
        ...monthlyExceptionQueries,
        [monthId]: {
          ...monthlyExceptionQueries[monthId],
          ...newDataProps,
        },
      },
    }
    : {};
};

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => payload => ({ type: actionType, payload, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/MerchantOnboardingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/MerchantOnboardingPage/CLEAR_UPDATED_TAB';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/MerchantOnboardingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/MerchantOnboardingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/MerchantOnboardingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/MerchantOnboardingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/MerchantOnboardingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/MerchantOnboardingPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/MerchantOnboardingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/MerchantOnboardingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/MerchantOnboardingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/MerchantOnboardingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/MerchantOnboardingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/MerchantOnboardingPage/SHOW_LISTINGS_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/MerchantOnboardingPage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/MerchantOnboardingPage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/MerchantOnboardingPage/QUERY_LISTINGS_ERROR';

export const QUERY_DEAL_LISTINGS_REQUEST = 'app/MerchantOnboardingPage/QUERY_DEAL_LISTINGS_REQUEST';
export const QUERY_DEAL_LISTINGS_SUCCESS = 'app/MerchantOnboardingPage/QUERY_DEAL_LISTINGS_SUCCESS';
export const QUERY_DEAL_LISTINGS_ERROR = 'app/MerchantOnboardingPage/QUERY_DEAL_LISTINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST = 'app/MerchantOnboardingPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS = 'app/MerchantOnboardingPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/MerchantOnboardingPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';
export const FETCH_EXTRA_EXCEPTIONS_SUCCESS =
  'app/MerchantOnboardingPage/FETCH_EXTRA_AVAILABILITY_EXCEPTIONS_SUCCESS';

export const ADD_EXCEPTION_REQUEST = 'app/MerchantOnboardingPage/ADD_AVAILABILITY_EXCEPTION_REQUEST';
export const ADD_EXCEPTION_SUCCESS = 'app/MerchantOnboardingPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS';
export const ADD_EXCEPTION_ERROR = 'app/MerchantOnboardingPage/ADD_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST = 'app/MerchantOnboardingPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS = 'app/MerchantOnboardingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR = 'app/MerchantOnboardingPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const SET_STOCK_REQUEST = 'app/MerchantOnboardingPage/SET_STOCK_REQUEST';
export const SET_STOCK_SUCCESS = 'app/MerchantOnboardingPage/SET_STOCK_SUCCESS';
export const SET_STOCK_ERROR = 'app/MerchantOnboardingPage/SET_STOCK_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/MerchantOnboardingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/MerchantOnboardingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/MerchantOnboardingPage/UPLOAD_IMAGE_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/MerchantOnboardingPage/REMOVE_LISTING_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/MerchantOnboardingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/MerchantOnboardingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/MerchantOnboardingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const UPDATE_BUSINESS_PROFILE_REQUEST =
  'app/MerchantOnboardingPage/UPDATE_BUSINESS_PROFILE_REQUEST';
export const UPDATE_BUSINESS_PROFILE_SUCCESS =
  'app/MerchantOnboardingPage/UPDATE_BUSINESS_PROFILE_REQUEST';
export const UPDATE_BUSINESS_PROFILE_ERROR = 'app/MerchantOnboardingPage/UPDATE_BUSINESS_PROFILE_REQUEST';

export const DEALS_CATEGORIES_REQUEST = 'app/MerchantOnboardingPage/DEALS_CATEGORIES_REQUEST';
export const DEALS_CATEGORIES_SUCCESS = 'app/MerchantOnboardingPage/DEALS_CATEGORIES_SUCCESS';
export const DEALS_CATEGORIES_ERROR = 'app/MerchantOnboardingPage/DEALS_CATEGORIES_ERROR';

export const CATEGORIES_REQUEST = 'app/MerchantOnboardingPage/CATEGORIES_REQUEST';
export const CATEGORIES_SUCCESS = 'app/MerchantOnboardingPage/CATEGORIES_SUCCESS';
export const CATEGORIES_ERROR = 'app/MerchantOnboardingPage/CATEGORIES_ERROR';

export const CREATE_LISTING_REQUEST = 'app/MerchantOnboardingPage/CREATE_LISTING_REQUEST';
export const CREATE_LISTING_SUCCESS = 'app/MerchantOnboardingPage/CREATE_LISTING_SUCCESS';
export const CREATE_LISTING_ERROR = 'app/MerchantOnboardingPage/CREATE_LISTING_ERROR';

export const FETCH_STOCK_IMAGES_REQUEST = 'app/MerchantOnboardingPage/FETCH_STOCK_IMAGES_REQUEST';
export const FETCH_STOCK_IMAGES_SUCCESS = 'app/MerchantOnboardingPage/FETCH_STOCK_IMAGES_SUCCESS';
export const FETCH_STOCK_IMAGES_ERROR = 'app/MerchantOnboardingPage/FETCH_STOCK_IMAGES_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  queryListingId: null,
  queryListingsError: null,
  queryDealListingsError: null,
  queryDealListingId: null,
  uploadImageError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  businessProfileRequest: false,
  businessProfileSuccess: null,
  businessProfileError: null,
  dealsCategoriesRequest: false,
  dealsCategoriesSuccess: [],
  dealsCategoriesError: null,
  categoriesRequest: false,
  categoriesSuccess: [],
  categoriesError: null,
  createListingInProgress: false,
  createdListingId: null,
  createListingError: null,
  stockImages: [],
  stockImagesFetchInProgress: false,
  stockImagesError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case CREATE_LISTING_REQUEST:
      return {
        ...state,
        createListingInProgress: true,
        createListingError: null,
      };

    case CREATE_LISTING_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingInProgress: false,
        createdListingId: payload.data.id,
      };
    case CREATE_LISTING_ERROR:
      return {
        ...state,
        createListingInProgress: false,
        createListingError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        listingId: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishListingError: {
          listingId: state.listingId,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        updateInProgress: false,
        // availabilityCalendar: { ...state.availabilityCalendar },
      };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };

    case QUERY_LISTINGS_REQUEST:
      return { ...state, queryListingsError: null };

    case QUERY_DEAL_LISTINGS_REQUEST:
      return { ...state, queryDealListingsError: null };

    case SHOW_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data.id;
      const { listingId, allExceptions, weeklyExceptionQueries, monthlyExceptionQueries } = state;
      // If listing stays the same, we trust previously fetched exception data.
      return listingIdFromPayload?.uuid === state.listingId?.uuid
        ? {
          ...initialState,
          listingId,
          allExceptions,
          weeklyExceptionQueries,
          monthlyExceptionQueries,
        }
        : { ...initialState, listingId: listingIdFromPayload };
    }

    case QUERY_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data?.[0]?.id;
      const { queryListingId, allExceptions, weeklyExceptionQueries, monthlyExceptionQueries } = state;
      // If listing stays the same, we trust previously fetched exception data.
      return { ...state }
    }

    case QUERY_DEAL_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data?.[0]?.id;
      return { ...state, queryDealListingId: listingIdFromPayload };
    }

    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case QUERY_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryListingsError: payload, redirectToListing: false };

    case QUERY_DEAL_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryDealListingsError: payload, redirectToListing: false };

    case FETCH_EXCEPTIONS_REQUEST: {
      const { monthId, weekStartId } = payload.params;
      const newData = { fetchExceptionsError: null, fetchExceptionsInProgress: true };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};
      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_SUCCESS: {
      const { exceptions, monthId, weekStartId } = payload;
      const combinedExceptions = state.allExceptions.concat(exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      const newData = { fetchExceptionsInProgress: false };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};
      return { ...state, allExceptions, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_ERROR: {
      const { monthId, weekStartId, error } = payload;
      const newData = { fetchExceptionsInProgress: false, fetchExceptionsError: error };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};

      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXTRA_EXCEPTIONS_SUCCESS: {
      const combinedExceptions = state.allExceptions.concat(payload.exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      // TODO: currently we don't handle thrown errors from these paginated calls
      return { ...state, allExceptions };
    }
    case ADD_EXCEPTION_REQUEST:
      return {
        ...state,
        addExceptionError: null,
        addExceptionInProgress: true,
      };
    case ADD_EXCEPTION_SUCCESS: {
      const exception = payload;
      const combinedExceptions = state.allExceptions.concat(exception);
      const allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
      return {
        ...state,
        allExceptions,
        addExceptionInProgress: false,
      };
    }
    case ADD_EXCEPTION_ERROR:
      return {
        ...state,
        addExceptionError: payload.error,
        addExceptionInProgress: false,
      };

    case DELETE_EXCEPTION_REQUEST:
      return {
        ...state,
        deleteExceptionError: null,
        deleteExceptionInProgress: true,
      };
    case DELETE_EXCEPTION_SUCCESS: {
      const exception = payload;
      const id = exception.id.uuid;
      const allExceptions = state.allExceptions.filter(e => e.id.uuid !== id);
      return {
        ...state,
        allExceptions,
        deleteExceptionInProgress: false,
      };
    }
    case DELETE_EXCEPTION_ERROR:
      return {
        ...state,
        deleteExceptionError: payload.error,
        deleteExceptionInProgress: false,
      };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, ...rest } };
      return { ...state, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
      const uploadedImages = omit(state.uploadedImages, id);
      return { ...state, uploadedImagesOrder, uploadedImages, uploadImageError: error };
    }

    case FETCH_STOCK_IMAGES_REQUEST:
      return { ...state, stockImagesFetchInProgress: true, stockImagesError: null };
    case FETCH_STOCK_IMAGES_SUCCESS: {
      const stockImages = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      return {
        ...state,
        stockImagesFetchInProgress: false,
        stockImages,
      };
    }
    case FETCH_STOCK_IMAGES_ERROR:
      return { ...state, stockImagesFetchInProgress: false, stockImagesError: payload };

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);

      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case SET_STOCK_REQUEST:
      return { ...state, setStockInProgress: true, setStockError: null };
    case SET_STOCK_SUCCESS:
      return { ...state, setStockInProgress: false };
    case SET_STOCK_ERROR:
      return { ...state, setStockInProgress: false, setStockError: payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case UPDATE_BUSINESS_PROFILE_REQUEST:
      return {
        ...state,
        businessProfileRequest: true,
      };

    case UPDATE_BUSINESS_PROFILE_SUCCESS:
      return {
        ...state,
        businessProfileRequest: false,
        businessProfileSuccess: payload,
      };

    case UPDATE_BUSINESS_PROFILE_ERROR:
      return {
        ...state,
        businessProfileError: payload,
      };

    case DEALS_CATEGORIES_REQUEST:
      return {
        ...state,
        dealsCategoriesRequest: true,
        dealsCategoriesSuccess: [],
      };

    case DEALS_CATEGORIES_SUCCESS:
      return {
        ...state,
        dealsCategoriesRequest: false,
        dealsCategoriesSuccess: payload,
      };

    case DEALS_CATEGORIES_ERROR:
      return {
        ...state,
        dealsCategoriesError: payload,
      };

    case CATEGORIES_REQUEST:
      return {
        ...state,
        categoriesRequest: true,
      };

    case CATEGORIES_SUCCESS:
      return {
        ...state,
        categoriesRequest: false,
        categoriesSuccess: Array.isArray(payload) ? payload : [payload],
      };

    case CATEGORIES_ERROR:
      return {
        ...state,
        categoriesError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const categoriesRequestFn = () => ({
  type: DEALS_CATEGORIES_REQUEST,
});

export const categoriesSuccessFn = payload => ({
  type: DEALS_CATEGORIES_SUCCESS,
  payload: payload,
});

export const categoriesErrorFn = response => ({
  type: DEALS_CATEGORIES_ERROR,
  payload: response,
});

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

const updateBusinessProfileRequestFn = () => ({
  type: UPDATE_BUSINESS_PROFILE_REQUEST,
});

const updateBusinessProfileSuccessFn = payload => ({
  type: UPDATE_BUSINESS_PROFILE_SUCCESS,
  payload,
});

const updateBusinessProfileErrorFn = payload => ({
  type: UPDATE_BUSINESS_PROFILE_ERROR,
  payload,
});

export const categoriesRequest = () => ({
  type: CATEGORIES_REQUEST,
});

export const categoriesSuccess = payload => ({
  type: CATEGORIES_SUCCESS,
  payload: payload,
});

export const categoriesError = response => ({
  type: CATEGORIES_ERROR,
  payload: response,
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraftRequest = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

export const createListingRequest = requestAction(CREATE_LISTING_REQUEST);
export const createListingSuccess = successAction(CREATE_LISTING_SUCCESS);
export const createListingError = errorAction(CREATE_LISTING_ERROR);

// SDK method: ownListings.publish
export const publishListingRequest = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListingRequest = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListingsRequest = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: ownListings.query
export const queryListingsRequest = requestAction(QUERY_LISTINGS_REQUEST);
export const queryListingsSuccess = successAction(QUERY_LISTINGS_SUCCESS);
export const queryListingsError = errorAction(QUERY_LISTINGS_ERROR);

// SDK method: ownListings.query
export const queryDealListingsRequest = requestAction(QUERY_DEAL_LISTINGS_REQUEST);
export const queryDealListingsSuccess = successAction(QUERY_DEAL_LISTINGS_SUCCESS);
export const queryDealListingsError = errorAction(QUERY_DEAL_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

export const fetchStockImagesRequest = requestAction(FETCH_STOCK_IMAGES_REQUEST);
export const fetchStockImagesSuccess = successAction(FETCH_STOCK_IMAGES_SUCCESS);
export const fetchStockImagesError = errorAction(FETCH_STOCK_IMAGES_ERROR);

// SDK method: stock.compareAndSet
export const setStockRequest = requestAction(SET_STOCK_REQUEST);
export const setStockSuccess = successAction(SET_STOCK_SUCCESS);
export const setStockError = errorAction(SET_STOCK_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);
// Add extra data from additional pages
export const fetchExtraAvailabilityExceptionsSuccess = successAction(
  FETCH_EXTRA_EXCEPTIONS_SUCCESS
);

// SDK method: availabilityExceptions.create
export const addAvailabilityExceptionRequest = requestAction(ADD_EXCEPTION_REQUEST);
export const addAvailabilityExceptionSuccess = successAction(ADD_EXCEPTION_SUCCESS);
export const addAvailabilityExceptionError = errorAction(ADD_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

// ================ Thunk ================ //

export const categoriesListingFn = () => async dispatch => {
  dispatch(categoriesRequest());
  return onGetCategories()
    .then(response => {
      dispatch(categoriesSuccess(response));
      return response;
    })
    .catch(error => {
      dispatch(categoriesError(error.message));
    });
};

export const categoriesListing = () => async dispatch => {
  dispatch(categoriesRequestFn);
  try {
    const response = await onGetDealsCategoriesList();
    dispatch(categoriesSuccessFn(response));
    return response;
  } catch (error) {
    dispatch(categoriesErrorFn(error.message));
  }
};

export function requestShowListing(actionPayload, config) {
  return (dispatch, getState, sdk) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(showListingsRequest(actionPayload));
    return sdk.ownListings
      .show({ ...actionPayload, ...queryParams })
      .then(response => {
        // MerchantOnboardingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.MerchantOnboardingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

// Set stock if requested among listing update info
export function compareAndSetStock(listingId, oldTotal, newTotal) {
  return (dispatch, getState, sdk) => {
    dispatch(setStockRequest());

    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then(response => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        dispatch(setStockSuccess(response));
      })
      .catch(e => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return dispatch(setStockError(storableError(e)));
      });
  };
}

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

export const updateBusinessProfile = body => async dispatch => {
  dispatch(updateBusinessProfileRequestFn());
  try {
    const response = await onUpdateBusinessProfile(body);
    dispatch(updateBusinessProfileSuccessFn(response));
  } catch (error) {
    dispatch(updateBusinessProfileErrorFn(error.message));
  }
};

// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export function requestCreateListingDraft(data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(createListingDraftRequest(data));
    const { stockUpdate, images, publicData, ...rest } = data;
    const { deal_start_date, deal_end_date } = publicData || {};

    // If images should be saved, create array out of the image UUIDs for the API call
    // Note: in this template, image upload is not happening at the same time as listing creation.
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    // Same platform stamp as EditListingPage.duck.js's requestCreateListingDraft
    // — this is the merchant onboarding flow's own separate copy of listing
    // creation, so it needs the same one-time stamp independently.
    const ownListingValues = {
      ...imageProperty,
      publicData: { ...publicData, platform: 'web' },
      ...rest,
    };

    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    let createDraftResponse = null;
    return sdk.ownListings
      .createDraft(ownListingValues, queryParams)
      .then(response => {
        createDraftResponse = response;
        const listingId = response.data.data.id;
        const listing = response.data.data;
        const { listingType } = (listing && listing.id && listing.attributes.publicData) || {};

        if (listingType == 'business-profile') {
          dispatch(fetchCurrentUserHasListingsSuccess(true, response.data.data));
        }
        // If stockUpdate info is passed through, update stock
        return updateStockOfListingMaybe(listingId, stockUpdate, dispatch);
      })
      .then(() => {
        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(createDraftResponse));
        return createDraftResponse;
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

// Create or Update listing in Algolia
const createListingInAlgolia = (response, currentUser = {}) => {
  const {
    title = '',
    description = '',
    price,
    geolocation,
    createdAt,
    publicData = {},
    metaData = {},
    availabilityPlan,
    state,
  } = response?.data?.data?.attributes || {};

  // if (state == "published") {
  const listingType = publicData?.listingType;
  const algoliaIndex = () => {
    switch (listingType) {
      case BUSINESS_PROFILE_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_BUSINESS_INDEX;
      case DEAL_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_DEALS_INDEX;
      case PRODUCT_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_PRODUCTS_INDEX;
      case SERVICE_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_SERVICE_INDEX;
      case STAFF_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_STAFF_INDEX;
      default:
        return '';
    }
  };

  const includedData = response?.data?.included || [];
  const images = includedData
    .filter(data => data?.type === 'image')
    .map(data => data?.attributes?.variants?.['listing-card-2x']?.url);

  const { email, profile } = currentUser?.attributes || {};

  const { firstName, lastName, displayName, publicData: currentUserPublicData } = profile || {};

  const currentUserPublicDatMaybe = !!currentUserPublicData
    ? { publicData: { ...currentUserPublicData } }
    : {};

  const geolocationMaybe = geolocation ? { _geoloc: { lat: geolocation?.lat, lng: geolocation?.lng } } : {};

  createAlgoliaObject({
    indexName: algoliaIndex(),
    objectID: response?.data?.data?.id?.uuid,
    title,
    description,
    price,
    images,
    ...geolocationMaybe,
    publicData: { ...publicData },
    metaData,
    createdAt,
    author: {
      id: currentUser?.id?.uuid,
      firstName,
      lastName,
      email,
      displayName,
      ...currentUserPublicDatMaybe,
    },
    availabilityPlan,
    state,
  });
  // }
};

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
export function requestUpdateListing(tab, data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(updateListingRequest(data));
    const { id, stockUpdate, images, publicData, ...rest } = data;

    const state = getState();
    const { currentUser, currentUserListing } = state?.user || {};
    const { profileListingId = '' } = currentUser?.attributes?.profile?.publicData || {};
    const businessListingIdMaybe = profileListingId ? { profileListingId } : {};
    const listingIdUuid = id?.uuid || id;
    const existingListingPublicData =
      state.marketplaceData?.entities?.ownListing?.[listingIdUuid]?.attributes?.publicData || {};


    const { geolocation: businessGeolocation, publicData: businessPublicData } = currentUserListing?.attributes || {};
    const { location: businessLocation } = businessPublicData || {};

    const geolocationMaybe = businessGeolocation ? { geolocation: businessGeolocation } : {};
    const locationMaybe = businessLocation ? { location: businessLocation } : {};

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingUpdateValues = {
      id,
      ...imageProperty,
      ...rest,
      ...geolocationMaybe,
      publicData: {
        ...existingListingPublicData,
        ...publicData,
        pickupEnabled: true,
        shippingEnabled: true,
        ...(publicData?.listingType !== BUSINESS_PROFILE_LISTING_TYPE ? businessListingIdMaybe : {}),
        ...(publicData?.listingType !== BUSINESS_PROFILE_LISTING_TYPE ? locationMaybe : {}),
      },
    };
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    const existingTimeZone =
      state.marketplaceData.entities.ownListing[id.uuid]?.attributes?.availabilityPlan?.timezone;
    const includedTimeZone = rest?.availabilityPlan?.timezone;

    // Note: if update values include stockUpdate, we'll do that first
    // That way we get updated currentStock info among ownListings.update
    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => sdk.ownListings.update(ownListingUpdateValues, queryParams))
      .then(response => {
        dispatch(updateListingSuccess(response));
        dispatch(addMarketplaceEntities(response));
        dispatch(markTabUpdated(tab));

        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed.
        if (!!includedTimeZone && includedTimeZone !== existingTimeZone) {
          const searchString = '';
          const firstDayOfWeek = config.localization.firstDayOfWeek;
          const listing = response.data.data;
          fetchLoadDataExceptions(dispatch, listing, searchString, firstDayOfWeek);
        }

        return response;
      })
      .then(response => {
        // Extract listing state and listing data from the response
        const listingData = response?.data?.data;
        const listingId = listingData?.id?.uuid;
        const { state: listingState, availabilityPlan = {} } = listingData?.attributes || {};
        const { listingType, categoryLevel2, deal_start_date, deal_end_date, availabilityExceptionId } =
          (listingData && listingData.id && listingData.attributes.publicData) || {};

        const categoryUpdateObj = {
          categoryId: categoryLevel2,
          businessListingId: profileListingId,
          operation: 'add',
        };

        if (listingType === DEAL_LISTING_TYPE && availabilityExceptionId) {
          // Delete old Availability exception
          dispatch(requestDeleteAvailabilityException({ id: availabilityExceptionId }))
            .then(() => {
              // Create new Availability exception
              const startDateInMs = deal_start_date * 1000;
              const endDateInMs = deal_end_date * 1000;
              const startMomentDate = moment.tz(startDateInMs, availabilityPlan?.timezone);
              const endMomentDate = moment.tz(endDateInMs, availabilityPlan?.timezone);
              const startOfDay = startMomentDate.startOf('day');
              const endOfDay = endMomentDate.startOf('day');
              const seats = 100;

              const range = {
                start: startOfDay.toISOString(),
                end: endOfDay.toISOString(),
              };

              const params = {
                listingId,
                seats,
                ...range,
              };

              dispatch(requestAddAvailabilityException(params))
                .then((data) => {
                  console.log("created exception on update deal", data);
                  const availabilityExceptionId = data && data?.id?.uuid;
                  if (availabilityExceptionId) {
                    dispatch(updateListing({ id: listingId, publicData: { availabilityExceptionId } }, config));
                  }
                })
                .catch(e => {
                  console.error('Failed to create availability exception on deal update:', e);
                })
            })

          if (Object.values(categoryUpdateObj).every(val => Boolean(val))) {
            onUpdateDealCategoriesProfile(categoryUpdateObj);
          }
        }
        else if (listingType === PRODUCT_LISTING_TYPE || listingType === SERVICE_LISTING_TYPE && Object.values(categoryUpdateObj).every(val => Boolean(val))) {
          onUpdateCategoriesProfile(categoryUpdateObj);
        }
        // Check if the listing is a business profile and dispatch success action
        else if (listingType === BUSINESS_PROFILE_LISTING_TYPE) {
          dispatch(fetchCurrentUserHasListingsSuccess(true, listingData));
        }

        // If the listing is published, create it in Algolia
        if (listingState !== LISTING_STATE_DRAFT) {
          // createListingInAlgolia(response, currentUser);
        }

        // Return the original response for further use
        return response;
      })

      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

export const requestPublishListingDraft = (listingId, config) => (dispatch, getState, sdk) => {
  dispatch(publishListingRequest(listingId));

  const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
  const queryParams = {
    expand: true,
    include: ['author', 'images', 'currentStock'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
  };

  return sdk.ownListings
    .publishDraft({ id: listingId }, queryParams)
    .then(response => {
      const state = getState();
      // createListingInAlgolia(response, state?.user?.currentUser);
      const profileListingId =
        state.user?.currentUser?.attributes?.profile?.publicData?.profileListingId;

      // Add the created listing to the marketplace data
      const listing = response.data.data;
      const { listingType, categoryLevel2, deal_start_date, deal_end_date } =
        (listing && listing.id && listing.attributes.publicData) || {};

      // DEBUG: confirm the listing was created/updated and with what state/category. A deal only
      // shows in search + counts when state === 'published'. If state is 'pendingApproval', it needs
      // admin approval first. listingsCategories must hold the linked service's subcategory id.
      console.log('🟢 [LISTING CREATE/UPDATE] Sharetribe result:', {
        listingId: listing?.id?.uuid,
        listingType,
        state: listing?.attributes?.state,
        categoryLevel2,
        listingsCategories: listing?.attributes?.publicData?.listingsCategories,
        title: listing?.attributes?.title,
      });

      const availabilityPlan = listing?.attributes?.availabilityPlan || {};
      if (listingType == BUSINESS_PROFILE_LISTING_TYPE) {
        dispatch(fetchCurrentUserHasListingsSuccess(true, response.data.data));
      }

      const categoryUpdateObj = {
        categoryId: categoryLevel2,
        businessListingId: profileListingId,
        operation: 'add',
      };

      // Dispatch actions to update categories in the user's profile
      if (listingType === DEAL_LISTING_TYPE) {
        // Create Availability exception for Deal
        const startDateInMs = deal_start_date * 1000;
        const endDateInMs = deal_end_date * 1000;
        const startMomentDate = moment.tz(startDateInMs, availabilityPlan?.timezone);
        const endMomentDate = moment.tz(endDateInMs, availabilityPlan?.timezone);
        const startOfDay = startMomentDate.startOf('day');
        const endOfDay = endMomentDate.startOf('day');
        const seats = 100;

        const range = {
          start: startOfDay.toISOString(),
          end: endOfDay.toISOString(),
        };
        const params = {
          listingId: listing?.id?.uuid,
          seats,
          ...range,
        };
        dispatch(requestAddAvailabilityException(params));
        // .then(res => console.log("created exception of deal", res));

        onUpdateDealCategoriesProfile(categoryUpdateObj);
      } else if (listingType === PRODUCT_LISTING_TYPE || listingType === SERVICE_LISTING_TYPE) {
        onUpdateCategoriesProfile(categoryUpdateObj);
      }

      if (listingType === STAFF_LISTING_TYPE) {
        dispatch(
          updateBusinessProfile({
            staffId: response.data.data?.id?.uuid,
            id: profileListingId,
          })
        );
      }

      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      return response;
    })
    .then(response => {
      const state = getState();

      return response;
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

// Images return imageId which we need to map with previously generated temporary id
export function requestImageUpload(actionPayload, listingImageConfig) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    const imageVariantInfo = getImageVariantInfo(listingImageConfig);
    const queryParams = {
      expand: true,
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(uploadImageRequest(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const img = resp.data.data;
        const uploadedImage = { ...img, id, imageId: img.id, file: actionPayload.file };
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        dispatch(uploadImageSuccess({ data: uploadedImage }));
        return uploadedImage;
      })
      .catch(e => {
        const error = storableError(e);
        dispatch(uploadImageError({ id, error }));
        throw error;
      });
  };
}

export const requestFetchStockImages =
  ({ categoryId, subcategoryId, limit = TEMPLATE_IMAGE_FETCH_LIMIT }) =>
  dispatch => {
    // Passed through as-is rather than parsed to an int: categories are
    // identified by slug now, so parseInt('home-services') was NaN and every
    // lookup bailed out to an empty gallery. The API accepts slug, uuid or a
    // legacy numeric string and decides which taxonomy to query.
    if (!categoryId || !subcategoryId) {
      return dispatch(fetchStockImagesSuccess({ data: [] }));
    }

    dispatch(
      fetchStockImagesRequest({
        categoryId,
        subcategoryId,
        limit,
      })
    );

    return onGetStockImages(categoryId, subcategoryId, { limit })
      .then(response => dispatch(fetchStockImagesSuccess(response)))
      .catch(error => dispatch(fetchStockImagesError(storableError(error))));
  };

export const requestUseTemplateImage =
  ({ templateImage, listingImageConfig }) =>
  dispatch => {
    const filePath = `/api/image-library/stock-images/${templateImage.id}/file`;
    const fileUrl = `${apiBaseUrl()}${filePath}`;

    return window
      .fetch(fileUrl, { credentials: 'include' })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(text || 'download_failed');
          });
        }
        return response.blob();
      })
      .then(blob => {
        const contentType = blob.type || 'image/jpeg';
        const ext =
          contentType === 'image/png'
            ? 'png'
            : contentType === 'image/webp'
              ? 'webp'
              : 'jpg';
        const file = new File([blob], `template_${templateImage.id}.${ext}`, { type: contentType });

        return dispatch(
          requestImageUpload(
            { id: `template_${templateImage.id}_${Date.now()}`, file },
            listingImageConfig
          )
        );
      });
  };

export const requestAddAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(addAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .create(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      dispatch(addAvailabilityExceptionSuccess({ data: availabilityException }));
      return availabilityException;
    })
    .catch(e => {
      dispatch(addAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestDeleteAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(deleteAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .delete(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return dispatch(deleteAvailabilityExceptionSuccess({ data: availabilityException }));
    })
    .catch(e => {
      dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestFetchAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  const { listingId, start, end, timeZone, page, isWeekly } = params;
  const fetchParams = { listingId, start, end };
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };
  dispatch(fetchAvailabilityExceptionsRequest(timeUnitIdProp));

  return sdk.availabilityExceptions
    .query(fetchParams)
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response);

      // Fetch potential extra exceptions pagination pages per month.
      // In theory, there could be several pagination pages worth of exceptions,
      // if range is month and unit is 'hour': 31 days * 24 hour = 744 slots for exceptions.
      const totalPages = response.data.meta.totalPages;
      if (totalPages > 1 && !page) {
        const extraPages = getArrayOfNItems(totalPages);

        // It's unlikely that this code is reached with default units.
        // Note:
        //  - Firing multiple API calls might hit API rate limit
        //    (This is very unlikely with this query and 'hour' unit.)
        //  - TODO: this doesn't take care of failures of those extra calls
        Promise.all(
          extraPages.map(page => {
            return sdk.availabilityExceptions.query({ ...fetchParams, page });
          })
        ).then(responses => {
          const denormalizedFlatResults = (all, r) => all.concat(denormalisedResponseEntities(r));
          const exceptions = responses.reduce(denormalizedFlatResults, []);
          dispatch(
            fetchExtraAvailabilityExceptionsSuccess({
              data: { ...timeUnitIdProp, exceptions },
            })
          );
        });
      }

      return dispatch(
        fetchAvailabilityExceptionsSuccess({
          data: { ...timeUnitIdProp, exceptions: availabilityExceptions },
        })
      );
    })
    .catch(e => {
      return dispatch(
        fetchAvailabilityExceptionsError({ ...timeUnitIdProp, error: storableError(e) })
      );
    });
};

// Helper function for loadData call.
const fetchLoadDataExceptions = (dispatch, listing, search, firstDayOfWeek) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone = listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const locationSearch = parse(search);
    const selectedDate = locationSearch?.d
      ? parseDateFromISO8601(locationSearch.d, timezone)
      : todayInListingsTZ;
    const startOfWeek = getStartOfWeek(selectedDate, timezone, firstDayOfWeek);
    const prevWeek = getStartOf(startOfWeek, 'day', timezone, -7, 'days');
    const nextWeek = getStartOf(startOfWeek, 'day', timezone, 7, 'days');
    const nextAfterNextWeek = getStartOf(nextWeek, 'day', timezone, 7, 'days');

    const nextMonth = getStartOf(todayInListingsTZ, 'month', timezone, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', timezone, 1, 'months');

    const sharedData = { listingId, timeZone: timezone };

    // Fetch data for selected week and nearest weeks for WeeklyCalendar
    // Plus current month and month after that for EditListingAvailabilityForm
    //
    // NOTE: This is making 5 different Thunk calls, which update store 2 times each
    //       It would make sense to make on thunk function that fires 5 sdk calls/promises,
    //       but for the time being, it's clearer to push all the calls through
    //       requestFetchAvailabilityExceptions
    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: prevWeek,
          end: startOfWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: startOfWeek,
          end: nextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: nextWeek,
          end: nextAfterNextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: nextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: nextMonth,
          end: nextAfterNextMonth,
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};
// export const showBussinessDetails = (params) => (dispatch, getState, sdk) => {
//   sdk.ownListings.query;
// };

// Upload file in Cloudinary
export const fileUploadCloudinary = file => (dispatch, getState, sdk) => {
  const uploadURL = process.env.REACT_APP_CLOUDINARY_URL;
  const formData = new FormData();
  const fileType = file.type;

  formData.append('file', file);
  formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
  formData.append('resource_type', fileType);
  formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);

  return fetch(uploadURL, { method: 'POST', body: formData })
    .then(response => response.text())
    .then(data => JSON.parse(data))
    .catch(e => {
      console.log(e);
      return {};
    });
};

export function requestQueryListings(actionPayload, config) {
  return (dispatch, getState, sdk) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(queryListingsRequest(actionPayload));
    return sdk.ownListings
      .query({ ...actionPayload, ...queryParams })
      .then(response => {
        // MerchantOnboardingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.MerchantOnboardingPage (user will be redirected away)
        dispatch(queryListingsSuccess(response));
        return response.data?.data;
      })
      .catch(e => dispatch(queryListingsError(storableError(e))));
  };
}

export function requestDealQueryListings(actionPayload, config) {
  return (dispatch, getState, sdk) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(queryDealListingsRequest(actionPayload));
    return sdk.ownListings
      .query({ ...actionPayload, ...queryParams })
      .then(response => {
        // MerchantOnboardingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.MerchantOnboardingPage (user will be redirected away)
        dispatch(queryDealListingsSuccess(response));
        return response.data?.data;
      })
      .catch(e => dispatch(queryDealListingsError(storableError(e))));
  };
}

/**
 * Marks the deal step as done without a deal, for a merchant who has nothing
 * that could back one (their only services are negotiated, which the deal
 * picker filters out — see AddDealForm's allItems).
 *
 * Deliberately a separate flag rather than setting isDealListingPublished:
 * that would be a lie, and dealListingId lookups, the deal review step and
 * deal analytics all read it. isMerchantOnboardingComplete treats the deal leg
 * as satisfied by either (see util/onboardingHelpers.js).
 */
export const skipDealStep = () => dispatch =>
  dispatch(updateProfile({ publicData: { dealStepSkipped: true } }));

export const createListing = (data, config) => (dispatch, getState, sdk) => {
  dispatch(createListingRequest(data));

  const { id, stockUpdate, images, publicData, ...rest } = data;

  const state = getState();
  const currentUser = state?.user?.currentUser || {};
  const { profileListingId = '', defaultStaffId } = currentUser?.attributes?.profile?.publicData || {};
  const businessListingIdMaybe = profileListingId ? { profileListingId } : {};

  // If images should be saved, create array out of the image UUIDs for the API call
  const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};

  const ownListingUpdateValues = {
    ...imageProperty,
    ...rest,
    publicData: {
      ...publicData,
      ...businessListingIdMaybe,
      pickupEnabled: true,
      shippingEnabled: true,
      // createListing is create-only (called once per listing, e.g. for
      // staff — see onCreateListing in MerchantOnboardingPage.js), so this
      // is a one-time stamp, same as the two requestCreateListingDraft copies.
      platform: 'web',
    },
  };

  const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
  const queryParams = {
    expand: true,
    include: ['author', 'images', 'currentStock'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
  };

  // SERVICE/PRODUCT/DEAL listings are created as drafts and published only after the
  // final business-profile step so they never appear in search mid-onboarding.
  const createAsDraft = [SERVICE_LISTING_TYPE, PRODUCT_LISTING_TYPE, DEAL_LISTING_TYPE].includes(
    publicData?.listingType
  );
  const sdkCreate = createAsDraft
    ? sdk.ownListings.createDraft(ownListingUpdateValues, queryParams)
    : sdk.ownListings.create(ownListingUpdateValues, queryParams);

  return sdkCreate
    .then(response => {
      // If stockUpdate info is passed through, update stock
      const listingId = response?.data?.data?.id?.uuid;
      updateStockOfListingMaybe(listingId, stockUpdate, dispatch);

      const state = getState();
      // Index immediately in Algolia. The background poller is the primary indexer in production but
      // is disabled in local dev, so without this a locally-created listing/deal never reaches the
      // index. saveObject is idempotent, so it's safe alongside the poller.
      createListingInAlgolia(response, state?.user?.currentUser);

      // Add the created listing to the marketplace data
      const listing = response.data.data;
      const { listingType, categoryLevel2, deal_start_date, deal_end_date } =
        (listing && listing.id && listing.attributes.publicData) || {};

      // DEBUG: confirm the listing was created/updated and with what state/category. A deal only
      // shows in search + counts when state === 'published'. If state is 'pendingApproval', it needs
      // admin approval first. listingsCategories must hold the linked service's subcategory id.
      console.log('🟢 [LISTING CREATE/UPDATE] Sharetribe result:', {
        listingId: listing?.id?.uuid,
        listingType,
        state: listing?.attributes?.state,
        categoryLevel2,
        listingsCategories: listing?.attributes?.publicData?.listingsCategories,
        title: listing?.attributes?.title,
      });

      const availabilityPlan = listing?.attributes?.availabilityPlan || {};

      if (listingType === DEAL_LISTING_TYPE && !createAsDraft) {
        // Create Availability exception for Deal (only for immediately-published deals;
        // draft deals get their exception created in requestPublishListingDraft)
        const startDateInMs = deal_start_date * 1000;
        const endDateInMs = deal_end_date * 1000;
        const startMomentDate = moment.tz(startDateInMs, availabilityPlan?.timezone);
        const endMomentDate = moment.tz(endDateInMs, availabilityPlan?.timezone);
        const startOfDay = startMomentDate.startOf('day');
        const endOfDay = endMomentDate.startOf('day');
        const seats = 100;

        const range = {
          start: startOfDay.toISOString(),
          end: endOfDay.toISOString(),
        };
        const params = {
          listingId: listing?.id?.uuid,
          seats,
          ...range,
        };

        dispatch(requestAddAvailabilityException(params))
          .then((data) => {
            // console.log("created exception on update deal", data);
            const availabilityExceptionId = data && data?.id?.uuid;
            if (availabilityExceptionId) {
              dispatch(updateListing({ id: listingId, publicData: { availabilityExceptionId } }, config));
            }
          })
          .catch(e => {
            console.error('Failed to create availability exception on deal create:', e);
          });
      }
      else if (listingType === STAFF_LISTING_TYPE) {
        dispatch(
          updateBusinessProfile({
            staffId: response.data.data?.id?.uuid,
            id: profileListingId,
          })
        );
      }
      dispatch(addMarketplaceEntities(response));

      return response;
    })
    .then(async response => {
      const listing = response.data.data;
      const profileListingId = listing?.id?.uuid;
      const { listingType } = (listing && listing.id && listing.attributes.publicData) || {};

      // Map listingType to the corresponding publicData field
      const publicDataUpdates = {
        [SERVICE_LISTING_TYPE]: { isProductServiceListingPublished: true },
        [PRODUCT_LISTING_TYPE]: { isProductServiceListingPublished: true },
        [DEAL_LISTING_TYPE]: { isDealListingPublished: true, dealStepSkipped: false },
        [BUSINESS_PROFILE_LISTING_TYPE]: { isBusinessListingPublished: true, profileListingId },
      };

      // Dynamically update publicData based on listingType
      if (publicDataUpdates[listingType]) {
        await dispatch(updateProfile({ publicData: publicDataUpdates[listingType] }));
      }

      // Fetch all listings and add location, availabilityPlan, profileListingId to all listings
      if (listingType === BUSINESS_PROFILE_LISTING_TYPE) {
        await dispatch(requestQueryListings({}, config))
          .then((data) => {
            const listings = !data ? [] : isArray(data) ? data : [data];

            const businessListing = response?.data?.data || {};
            const { geolocation, availabilityPlan, publicData, title: businessTitle } = businessListing?.attributes || {};
            const { location } = publicData || {};

            const geolocationMaybe = geolocation ? { geolocation } : {};
            const locationMaybe = location ? { location } : {};

            const { email: merchantEmail, profile } = currentUser?.attributes || {};
            const { protectedData } = profile || {};
            const { mobile_phone_number, phoneNumber } = protectedData || {};

            // get all categories and deal-categories to store it in Business Listing
            const categories = [];
            const dealCategories = [];
            listings.map(item => {
              const itemType = item?.attributes?.publicData?.listingType;
              const itemCategory = item?.attributes?.publicData?.categoryLevel2;

              if ([SERVICE_LISTING_TYPE, PRODUCT_LISTING_TYPE].includes(itemType)) {
                categories.push(itemCategory);
              }
              else if (itemType === DEAL_LISTING_TYPE) {
                dealCategories.push(itemCategory);
              }
            });

            // /////////////////
            listings.map(listing => {
              const { listingType } = (listing && listing?.attributes?.publicData) || {};
              const listingId = listing?.id?.uuid;
              const listingTitle = listing?.attributes?.title;

              // If service is created on onboarding then add location, availabilityPlan, profileListingId of business in it
              if (listingType === SERVICE_LISTING_TYPE) {
                const updatedData = {
                  ...geolocationMaybe,
                  availabilityPlan,
                  publicData: {
                    ...locationMaybe,
                    profileListingId,
                  },
                }

                if (listingId && availabilityPlan) {
                  const updatePromise = dispatch(requestUpdateListing("", { id: listingId, ...updatedData }, config));
                  // Publish the draft once location + availability are set
                  if (listing?.attributes?.state === 'draft') {
                    updatePromise.then(() => dispatch(requestPublishListingDraft(listingId, config)));
                  }
                }

                // Create default staff for this service
                if (listingId && availabilityPlan && !defaultStaffId) {
                  const staffDetails = {
                    title: businessTitle || "Staff",
                    publicData: {
                      email: merchantEmail,
                      mobile_phone_number: mobile_phone_number,
                      phoneNumber: phoneNumber,
                      profileListingId,
                      serviceIds: [listingId],
                      services: [{
                        key: listingId,
                        label: listingTitle,
                      }],
                      isDefault: true,
                      listingType: STAFF_LISTING_TYPE,
                      transactionProcessAlias: "default-booking/release-1",
                      unitType: "hour",
                    },
                    availabilityPlan,
                  };

                  dispatch(createListing(staffDetails, config))
                    .then(res => {
                      const staffId = res?.data?.data?.id?.uuid;
                      dispatch(updateProfile({ publicData: { defaultStaffId: staffId } }));
                      // STAFF listings are created via sdk.ownListings.create (not
                      // createDraft), so they're already published -- no publish step
                      // needed here. Publishing again re-fires createListing's own
                      // STAFF success handler (which already called
                      // updateBusinessProfile above), duplicating the staffId on the
                      // business listing.
                    })
                    .catch(err => {
                      console.log("error creating staff", err);
                    })
                }
              }
              else {
                // No need to add availability plan in listing other than service and staff
                const updatedData = {
                  ...geolocationMaybe,
                  publicData: {
                    ...locationMaybe,
                    profileListingId,
                  },
                }

                const updatePromise = dispatch(requestUpdateListing("", { id: listingId, ...updatedData }, config));
                // Publish draft product/deal listings (requestPublishListingDraft also
                // creates the availability exception for deals)
                const draftPublishTypes = [PRODUCT_LISTING_TYPE, DEAL_LISTING_TYPE];
                if (listing?.attributes?.state === 'draft' && draftPublishTypes.includes(listingType)) {
                  updatePromise.then(() => dispatch(requestPublishListingDraft(listingId, config)));
                }
              }
            });

            // At last update the business listing with categories [] and dealCategories []
            const updatedData = {
              id: profileListingId,
              publicData: {
                categories,
                dealCategories,
              },
            }
            dispatch(requestUpdateListing("", updatedData, config))
          })
      }

      await dispatch(fetchCurrentUser());
      dispatch(createListingSuccess(response));
      return response;
    })
    .catch((e) => {
      console.log(e);
      return dispatch(createListingError(storableError(e)));
    });
}

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());

  const { id, formType } = params;

  if (formType === 'new' || id === draftId) {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser())])
      .then(async response => {
        const { profileListingId } = response?.data?.data.attributes.profile.publicData || '';
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }

        dispatch(categoriesListing());
        dispatch(categoriesListingFn());
        // dispatch(showBussinessDetails(profileListingId));
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  if (id) {
    const payload = { id: new UUID(id) };
    return Promise.all([dispatch(requestShowListing(payload, config)), dispatch(fetchCurrentUser())])
      .then(async response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }

        // Because of two dispatch functions, response is an array.
        // We are only interested in the response from requestShowListing here,
        // so we need to pick the first one
        const listing = response[0]?.data?.data;
        const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
        if (listing && isBookingProcessAlias(transactionProcessAlias)) {
          fetchLoadDataExceptions(dispatch, listing, search, config.localization.firstDayOfWeek);
        }

        dispatch(categoriesListing());
        dispatch(categoriesListingFn());

        return response;
      })
      .catch(e => {
        throw e;
      });
  }
  else {
    return new Promise((resolve, reject) => {
      resolve()
    });
  }
};
